/**
 * Admin Feedback Page
 *
 * Paginated list of user feedback with type/status filters,
 * inline status & priority updates, and expandable detail view.
 * Access: platform_admin, platform_support
 */

import { useMemo, useState } from "react";
import {
  Bug,
  ChatCircle,
  CaretDown as ChevronDown,
  CaretRight as ChevronRight,
  Clock,
  Lightbulb,
  ArrowsClockwise as RefreshCw,
  FloppyDisk as Save,
  Star,
  Trash,
  User,
  WarningCircle,
  X,
} from "@phosphor-icons/react";
import {
  DataTable,
  DataTableBody,
  DataTableHead,
  DataTableRow,
  EmptyState,
  HeaderAction,
  PageHeader,
  type PillTone,
  StatusPill,
} from "@charisol-spark/ui";
import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import {
  AdminListToolbar,
  type ToolbarSortOption,
} from "@/components/lists/AdminListToolbar";
import {
  type ListUrlConfig,
  sortRows,
  useListUrlState,
} from "@/components/lists/useListUrlState";
import { useLoadMore } from "@/components/lists/useLoadMore";
import { Spinner } from "@/components/ui/Spinner";
import {
  useAdminFeedback,
  useDeleteAdminFeedback,
  useUpdateAdminFeedback,
} from "@/hooks/useAdmin";
import { hasRole } from "@/components/auth/AdminGuard";
import type { AdminFeedbackItem } from "@/services/admin";
import { cn } from "@/utils/cn";

// ── Filter options ─────────────────────────────────────────

const TYPE_OPTIONS = [
  { value: "", label: "All types" },
  { value: "rating", label: "Rating" },
  { value: "bug", label: "Bug" },
  { value: "feature_request", label: "Feature" },
  { value: "error_report", label: "Error" },
  { value: "general", label: "General" },
] as const;

const STATUS_OPTIONS = [
  { value: "", label: "All statuses" },
  { value: "open", label: "Open" },
  { value: "acknowledged", label: "Acknowledged" },
  { value: "in_progress", label: "In progress" },
  { value: "resolved", label: "Resolved" },
  { value: "wont_fix", label: "Won't fix" },
] as const;

const PRIORITY_OPTIONS = [
  { value: "", label: "Any priority" },
  { value: "low", label: "Low" },
  { value: "medium", label: "Medium" },
  { value: "high", label: "High" },
  { value: "critical", label: "Critical" },
] as const;

const PRIORITY_RANK: Record<string, number> = {
  low: 0,
  medium: 1,
  high: 2,
  critical: 3,
};

const SORT_OPTIONS: readonly ToolbarSortOption[] = [
  { field: "createdAt", label: "Received" },
  { field: "priority", label: "Priority", kind: "number" },
  { field: "status", label: "Status", kind: "text" },
];

/** Newest feedback first unless the address says otherwise. */
const URL_CONFIG: ListUrlConfig = {
  filterKeys: [
    "type",
    "status",
    "priority",
    "userId",
    "createdFrom",
    "createdTo",
  ],
  sortFields: SORT_OPTIONS.map((o) => o.field),
  defaultSort: { field: "createdAt", dir: "desc" },
};

const SORT_GETTERS: Record<
  string,
  (item: AdminFeedbackItem) => string | number | undefined
> = {
  createdAt: (i) => i.createdAt,
  priority: (i) => PRIORITY_RANK[i.priority] ?? 0,
  status: (i) => i.status,
};

/** Whether an item passes the filters the server cannot apply for us. */
function passesPageFilters(
  item: AdminFeedbackItem,
  search: string,
  filters: Record<string, string>
): boolean {
  if (filters.priority && item.priority !== filters.priority) return false;
  if (filters.userId && !item.userId.startsWith(filters.userId)) return false;
  const day = item.createdAt.slice(0, 10);
  if (filters.createdFrom && day < filters.createdFrom) return false;
  if (filters.createdTo && day > filters.createdTo) return false;
  if (search) {
    const text = [item.title, item.description, item.adminNotes]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();
    if (!text.includes(search.toLowerCase())) return false;
  }
  return true;
}

// ── Styling helpers ────────────────────────────────────────

const TYPE_ICONS: Record<string, React.ElementType> = {
  rating: Star,
  bug: Bug,
  feature_request: Lightbulb,
  error_report: WarningCircle,
  general: ChatCircle,
};

const TYPE_TONES: Record<string, PillTone> = {
  rating: "amber",
  bug: "red",
  feature_request: "violet",
  error_report: "orange",
  general: "blue",
};

const STATUS_TONES: Record<string, PillTone> = {
  open: "blue",
  acknowledged: "amber",
  in_progress: "violet",
  resolved: "green",
  wont_fix: "neutral",
};

const PRIORITY_TONES: Record<string, PillTone> = {
  low: "neutral",
  medium: "blue",
  high: "orange",
  critical: "red",
};

const LIST_COLUMNS = "minmax(0,1fr) 120px 130px 100px 24px";

function formatDate(iso?: string) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatLabel(s: string) {
  return s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

// ── Detail panel ───────────────────────────────────────────

function FeedbackDetail({
  item,
  onClose,
}: {
  item: AdminFeedbackItem;
  onClose: () => void;
}) {
  const updateMutation = useUpdateAdminFeedback();
  const deleteMutation = useDeleteAdminFeedback();
  const isAdmin = hasRole("platform_admin");

  const [status, setStatus] = useState(item.status);
  const [priority, setPriority] = useState(item.priority);
  const [adminNotes, setAdminNotes] = useState(item.adminNotes ?? "");
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);

  const hasChanges =
    status !== item.status ||
    priority !== item.priority ||
    adminNotes !== (item.adminNotes ?? "");

  function handleSave() {
    const data: { status?: string; priority?: string; adminNotes?: string } =
      {};
    if (status !== item.status) data.status = status;
    if (priority !== item.priority) data.priority = priority;
    if (adminNotes !== (item.adminNotes ?? "")) data.adminNotes = adminNotes;

    updateMutation.mutate({ feedbackId: item.feedbackId, data });
  }

  function handleDelete() {
    deleteMutation.mutate(item.feedbackId, { onSuccess: onClose });
  }

  const TypeIcon = TYPE_ICONS[item.type] ?? ChatCircle;

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/40">
      <div className="absolute inset-0" onClick={onClose} aria-hidden="true" />
      <div className="relative z-10 flex h-full w-full max-w-lg flex-col border-l border-border/50 bg-card shadow-xl sm:max-w-xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-border px-4 py-3 sm:px-6">
          <div className="flex items-center gap-2">
            <TypeIcon className="h-5 w-5 text-muted-foreground" />
            <h2 className="text-lg font-semibold">Feedback Detail</h2>
          </div>
          <button
            onClick={onClose}
            className="rounded-full p-1.5 text-muted-foreground hover:bg-accent hover:text-foreground"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-4 py-4 sm:px-6">
          <div className="space-y-5">
            {/* Meta badges */}
            <div className="flex flex-wrap gap-2">
              <StatusPill tone={TYPE_TONES[item.type] ?? "neutral"}>
                {formatLabel(item.type)}
              </StatusPill>
              <StatusPill tone={STATUS_TONES[item.status] ?? "neutral"}>
                {formatLabel(item.status)}
              </StatusPill>
              <StatusPill tone={PRIORITY_TONES[item.priority] ?? "neutral"}>
                {formatLabel(item.priority)}
              </StatusPill>
              {item.rating && (
                <StatusPill tone="amber">
                  <Star className="mr-0.5 h-3 w-3" weight="fill" />
                  {item.rating}/5
                </StatusPill>
              )}
            </div>

            {/* Title */}
            {item.title && (
              <h3 className="text-base font-semibold">{item.title}</h3>
            )}

            {/* Description */}
            <div>
              <p className="text-xs font-medium text-muted-foreground uppercase mb-1">
                Description
              </p>
              <p className="text-sm whitespace-pre-wrap">{item.description}</p>
            </div>

            {/* Error details */}
            {item.errorMessage && (
              <div>
                <p className="text-xs font-medium text-muted-foreground uppercase mb-1">
                  Error Message
                </p>
                <pre className="overflow-x-auto rounded-lg bg-muted p-3 text-xs">
                  {item.errorMessage}
                </pre>
              </div>
            )}

            {item.stackTrace && (
              <div>
                <p className="text-xs font-medium text-muted-foreground uppercase mb-1">
                  Stack Trace
                </p>
                <pre className="max-h-40 overflow-auto rounded-lg bg-muted p-3 text-xs">
                  {item.stackTrace}
                </pre>
              </div>
            )}

            {/* Metadata */}
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <p className="text-xs text-muted-foreground">User ID</p>
                <p className="font-mono text-xs">
                  {item.userId.slice(0, 12)}...
                </p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Created</p>
                <p className="text-xs">{formatDate(item.createdAt)}</p>
              </div>
              {item.pageUrl && (
                <div className="col-span-2">
                  <p className="text-xs text-muted-foreground">Page URL</p>
                  <p className="truncate text-xs">{item.pageUrl}</p>
                </div>
              )}
              {item.userAgent && (
                <div className="col-span-2">
                  <p className="text-xs text-muted-foreground">User Agent</p>
                  <p className="truncate text-xs">{item.userAgent}</p>
                </div>
              )}
            </div>

            {/* Admin controls */}
            <div className="space-y-3 rounded-xl border border-border/50 bg-card p-4">
              <p className="text-sm font-semibold">Admin Actions</p>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label
                    htmlFor="fb-status"
                    className="mb-1 block text-xs font-medium text-muted-foreground"
                  >
                    Status
                  </label>
                  <select
                    id="fb-status"
                    value={status}
                    onChange={(e) => setStatus(e.target.value)}
                    className="w-full rounded-lg border border-input bg-background px-2.5 py-1.5 text-sm focus-visible:border-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/30"
                  >
                    {STATUS_OPTIONS.filter((s) => s.value).map((s) => (
                      <option key={s.value} value={s.value}>
                        {s.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label
                    htmlFor="fb-priority"
                    className="mb-1 block text-xs font-medium text-muted-foreground"
                  >
                    Priority
                  </label>
                  <select
                    id="fb-priority"
                    value={priority}
                    onChange={(e) => setPriority(e.target.value)}
                    className="w-full rounded-lg border border-input bg-background px-2.5 py-1.5 text-sm focus-visible:border-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/30"
                  >
                    {["low", "medium", "high", "critical"].map((p) => (
                      <option key={p} value={p}>
                        {formatLabel(p)}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label
                  htmlFor="fb-notes"
                  className="mb-1 block text-xs font-medium text-muted-foreground"
                >
                  Admin Notes
                </label>
                <textarea
                  id="fb-notes"
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                  rows={3}
                  maxLength={5000}
                  className="w-full rounded-lg border border-input bg-background px-2.5 py-1.5 text-sm focus-visible:border-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/30 resize-none"
                  placeholder="Internal notes about this feedback..."
                />
              </div>

              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  pill
                  onClick={handleSave}
                  disabled={!hasChanges || updateMutation.isPending}
                >
                  {updateMutation.isPending ? (
                    <Spinner size="sm" className="mr-1.5" />
                  ) : (
                    <Save className="mr-1.5 h-3.5 w-3.5" />
                  )}
                  Save Changes
                </Button>

                {isAdmin && !showConfirmDelete && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowConfirmDelete(true)}
                    className="text-destructive hover:bg-destructive/10"
                  >
                    <Trash className="mr-1.5 h-3.5 w-3.5" />
                    Delete
                  </Button>
                )}

                {isAdmin && showConfirmDelete && (
                  <>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={handleDelete}
                      disabled={deleteMutation.isPending}
                    >
                      {deleteMutation.isPending ? (
                        <Spinner size="sm" className="mr-1.5" />
                      ) : (
                        "Confirm Delete"
                      )}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowConfirmDelete(false)}
                    >
                      Cancel
                    </Button>
                  </>
                )}
              </div>

              {updateMutation.isError && (
                <p className="text-xs text-destructive">
                  Failed to update. Please try again.
                </p>
              )}
              {updateMutation.isSuccess && (
                <p className="text-xs text-success">Updated successfully.</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Main page ──────────────────────────────────────────────

export function AdminFeedbackPage() {
  const list = useListUrlState(URL_CONFIG);
  const { filters } = list;
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const pager = useLoadMore<AdminFeedbackItem>(
    JSON.stringify([filters.type, filters.status])
  );

  const { data, isLoading, error, refetch, isFetching } = useAdminFeedback({
    type: filters.type || undefined,
    status: filters.status || undefined,
    cursor: pager.cursor,
    limit: 25,
  });

  const loadedItems = useMemo(
    () => [...pager.previous, ...(data?.items ?? [])],
    [pager.previous, data?.items]
  );
  const displayItems = useMemo(
    () =>
      sortRows(
        loadedItems.filter((item) =>
          passesPageFilters(item, list.search, filters)
        ),
        list.sort,
        SORT_GETTERS
      ),
    [loadedItems, list.search, list.sort, filters]
  );

  const expandedItem = expandedId
    ? displayItems.find((i) => i.feedbackId === expandedId)
    : null;

  return (
    <div className="space-y-6">
      <PageHeader
        variant="classic"
        title="Feedback"
        description="Review and manage user feedback, bug reports, and feature requests."
        action={
          <HeaderAction
            icon={
              <RefreshCw
                className={cn("h-4 w-4", isFetching && "animate-spin")}
              />
            }
            onClick={() => refetch()}
            disabled={isFetching}
          >
            Refresh
          </HeaderAction>
        }
      />

      <AdminListToolbar
        label="Feedback"
        search={{
          value: list.search,
          onChange: list.setSearch,
          placeholder: "Search title, text or notes...",
          scope: "page",
        }}
        filters={[
          {
            id: "type",
            label: "Type",
            kind: "select",
            options: TYPE_OPTIONS,
            value: filters.type!,
            onChange: (v) => list.setFilter("type", v),
          },
          {
            id: "status",
            label: "Status",
            kind: "select",
            options: STATUS_OPTIONS,
            value: filters.status!,
            onChange: (v) => list.setFilter("status", v),
          },
          {
            id: "priority",
            label: "Priority",
            kind: "select",
            options: PRIORITY_OPTIONS,
            value: filters.priority!,
            scope: "page",
            onChange: (v) => list.setFilter("priority", v),
          },
          {
            id: "userId",
            label: "User ID",
            kind: "text",
            placeholder: "Starts with...",
            value: filters.userId!,
            scope: "page",
            onChange: (v) => list.setFilter("userId", v.trim()),
          },
          {
            id: "createdFrom",
            label: "Received from",
            kind: "date",
            value: filters.createdFrom!,
            scope: "page",
            onChange: (v) => list.setFilter("createdFrom", v),
          },
          {
            id: "createdTo",
            label: "Received to",
            kind: "date",
            value: filters.createdTo!,
            scope: "page",
            onChange: (v) => list.setFilter("createdTo", v),
          },
        ]}
        sort={{
          options: SORT_OPTIONS,
          value: list.sort,
          onChange: list.setSort,
          scope: "page",
        }}
        hasActive={list.hasActive}
        onClear={list.clear}
      />

      {isLoading ? (
        <div className="flex h-40 items-center justify-center">
          <Spinner size="md" />
        </div>
      ) : error ? (
        <Alert variant="error">Failed to load feedback.</Alert>
      ) : (
        <>
          <DataTable>
            <DataTableHead columns={LIST_COLUMNS}>
              <span>Feedback</span>
              <span>Type</span>
              <span>Status</span>
              <span>Priority</span>
              <span />
            </DataTableHead>
            <DataTableBody>
              {displayItems.map((item) => {
                const TypeIcon = TYPE_ICONS[item.type] ?? ChatCircle;
                return (
                  <DataTableRow
                    key={item.feedbackId}
                    columns={LIST_COLUMNS}
                    onClick={() => setExpandedId(item.feedbackId)}
                  >
                    <div
                      role="button"
                      tabIndex={0}
                      className="flex min-w-0 items-start gap-3 rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          setExpandedId(item.feedbackId);
                        }
                      }}
                    >
                      <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-muted">
                        <TypeIcon className="h-4 w-4 text-muted-foreground" />
                      </div>
                      <div className="min-w-0">
                        <p className="truncate font-medium">
                          {item.title || item.description.slice(0, 80)}
                          {!item.title && item.description.length > 80
                            ? "..."
                            : ""}
                        </p>
                        <div className="mt-1 flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                          <span className="inline-flex items-center gap-1">
                            <User className="h-3 w-3" />
                            {item.userId.slice(0, 8)}...
                          </span>
                          <span className="inline-flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {formatDate(item.createdAt)}
                          </span>
                          {item.rating && (
                            <span className="inline-flex items-center gap-0.5">
                              <Star
                                className="h-3 w-3 text-amber-500"
                                weight="fill"
                              />
                              {item.rating}/5
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div>
                      <StatusPill tone={TYPE_TONES[item.type] ?? "neutral"}>
                        {formatLabel(item.type)}
                      </StatusPill>
                    </div>
                    <div>
                      <StatusPill tone={STATUS_TONES[item.status] ?? "neutral"}>
                        {formatLabel(item.status)}
                      </StatusPill>
                    </div>
                    <div>
                      <StatusPill
                        tone={PRIORITY_TONES[item.priority] ?? "neutral"}
                        className="capitalize"
                      >
                        {item.priority}
                      </StatusPill>
                    </div>
                    <div className="hidden justify-end md:flex">
                      {expandedId === item.feedbackId ? (
                        <ChevronDown className="h-4 w-4 text-muted-foreground" />
                      ) : (
                        <ChevronRight className="h-4 w-4 text-muted-foreground" />
                      )}
                    </div>
                  </DataTableRow>
                );
              })}
            </DataTableBody>
            {displayItems.length === 0 && (
              <EmptyState
                icon={ChatCircle}
                title={
                  list.hasActive
                    ? "No feedback matches your filters."
                    : "No feedback items found."
                }
                description="Try different filters, or load more rows."
              />
            )}
          </DataTable>

          {data?.cursor && (
            <div className="flex justify-center">
              <Button
                variant="outline"
                size="sm"
                pill
                onClick={() => pager.loadMore(data?.items ?? [], data?.cursor)}
              >
                Load More
              </Button>
            </div>
          )}
        </>
      )}

      {/* Detail drawer */}
      {expandedItem && (
        <FeedbackDetail
          item={expandedItem}
          onClose={() => setExpandedId(null)}
        />
      )}
    </div>
  );
}
